import { Component } from '@theme/component';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/** How long a settled quick-size button keeps showing before it reverts to plain text. */
const QUICK_SIZE_SETTLE_TIMEOUT = 8000;
/** How long the "added" tick stays visible before reverting to the size text. */
const QUICK_SIZE_ADDED_DURATION = 2000;

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {AbortController | undefined} */
  #changeEventAbortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  connectedCallback() {
    super.connectedCallback();

    // Custom elements can be disconnected and reconnected without being destroyed - e.g. when
    // morph() repositions this element among reordered siblings (see assets/morph.js's
    // updateChildren, which moves matched nodes via insertBefore rather than always replacing
    // them). Reset per-instance state on every connect so a reconnect doesn't duplicate entries
    // in these arrays or, further down, stack another 'change' listener on the same node.
    this.#checkedIndices = [];
    this.#radios = [];

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.#changeEventAbortController = new AbortController();
    this.addEventListener('change', this.variantChanged.bind(this), {
      signal: this.#changeEventAbortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#changeEventAbortController?.abort();
    this.#abortController?.abort();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    // The PLP quick-size widget: selecting a size both is the "add to cart" action and
    // needs to update in place. Handle it entirely without a network fetch for the variant
    // picker itself — see #handleQuickSizeSelection.
    const isSizePickerInProductCard = this.dataset.sizePicker === 'true' && !!event.target.closest('product-card');

    if (isSizePickerInProductCard) {
      this.#handleQuickSizeSelection(event, selectedOption);
      event.stopPropagation();
      return;
    }

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Handles a size selection in the PLP quick-size widget. Selecting a size here both
   * selects the variant and adds it to cart, so this updates everything the shopper needs
   * to see (id input, price, in-button loading/added state) from data attributes already
   * present on the radio, and submits the quick-add form directly — no network fetch of
   * the product page is needed for this path (that fetch was the source of the slow/hanging
   * PLP quick-add behaviour: a full product page fetch just to read one variant id).
   * @param {Event} event - The variant change event.
   * @param {HTMLElement} selectedOption - The selected radio input.
   */
  #handleQuickSizeSelection(event, selectedOption) {
    const variantId = selectedOption.dataset.variantId || null;
    const productCard = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement ? event.target.closest('product-card') : null);
    const button = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement ? event.target.closest('.variant-option__button-label') : null);
    const quickAddForm = productCard?.querySelector('product-form-component form');
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      quickAddForm?.querySelector('input[name="id"]') ?? null
    );

    if (variantIdInput && variantId) {
      variantIdInput.value = variantId;
      variantIdInput.setAttribute('value', variantId);
      variantIdInput.removeAttribute('disabled');
    }

    this.#updateQuickSizePrice(productCard, selectedOption);

    // Only one quick-size button can be "loading"/"added" at a time per product card —
    // clear any other button's state before marking this one as loading.
    productCard
      ?.querySelectorAll('.variant-option__button-label--loading, .variant-option__button-label--added')
      .forEach((other) => {
        if (other !== button) other.classList.remove('variant-option__button-label--loading', 'variant-option__button-label--added');
      });

    button?.classList.remove('variant-option__button-label--added');
    button?.classList.add('variant-option__button-label--loading');

    if (!quickAddForm || !variantId) return;

    /** @type {ReturnType<typeof setTimeout> | null} */
    let safetyTimeout = null;

    const settle = (/** @type {Event} */ settleEvent) => {
      if (safetyTimeout) {
        clearTimeout(safetyTimeout);
        safetyTimeout = null;
      }

      button?.classList.remove('variant-option__button-label--loading');

      const didError = /** @type {CustomEvent} */ (settleEvent)?.detail?.data?.didError;
      if (didError) return;

      button?.classList.add('variant-option__button-label--added');
      setTimeout(() => button?.classList.remove('variant-option__button-label--added'), QUICK_SIZE_ADDED_DURATION);
    };

    productCard?.addEventListener(ThemeEvents.cartUpdate, settle, { once: true });
    // Guarantee the button can never stay stuck loading — e.g. a network error that never
    // reaches product-form.js's cart:update dispatch.
    safetyTimeout = setTimeout(() => {
      productCard?.removeEventListener(ThemeEvents.cartUpdate, settle);
      settle(new CustomEvent(ThemeEvents.cartUpdate, { detail: { data: { didError: true } } }));
    }, QUICK_SIZE_SETTLE_TIMEOUT);

    const submitEvent = new CustomEvent('submit', {
      bubbles: true,
      cancelable: true,
      detail: { variantId: String(variantId) },
    });
    quickAddForm.dispatchEvent(submitEvent);
  }

  /**
   * Updates the visible price in a product card from the data attributes rendered onto the
   * selected radio, avoiding a fetch just to refresh the price on a size-only variant change.
   * Doesn't handle VIP-discount pricing or unit pricing — those stay as last rendered until
   * the next full page load, which is an acceptable trade-off for the common case.
   * @param {HTMLElement | null | undefined} productCard
   * @param {HTMLElement} selectedOption
   */
  #updateQuickSizePrice(productCard, selectedOption) {
    if (!productCard) return;

    const newPrice = selectedOption.dataset.variantPrice;
    if (!newPrice) return;

    const newComparePrice = selectedOption.dataset.variantCompareAtPrice || null;

    productCard.querySelectorAll('product-price [ref="priceContainer"]').forEach((container) => {
      const priceEl = container.querySelector('.price');
      if (priceEl) priceEl.textContent = newPrice;

      const compareGroup = container.querySelector('.compare-at-price')?.closest('[role="group"]');
      const compareEl = compareGroup?.querySelector('.compare-at-price');

      if (compareGroup instanceof HTMLElement) {
        if (newComparePrice && compareEl) {
          compareEl.textContent = newComparePrice;
          compareGroup.hidden = false;
        } else {
          compareGroup.hidden = true;
        }
      }
    });
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;

    // A hover/pointerenter prefetch of the same product page may already be in flight on
    // the sibling quick-add-component — this click-triggered fetch is higher intent, so
    // cancel the speculative one rather than let both race for the same response.
    const quickAddComponent = this.closest('product-card')?.querySelector('quick-add-component');
    if (quickAddComponent && typeof quickAddComponent.abortProductPageFetch === 'function') {
      quickAddComponent.abortProductPageFetch();
    }

    fetch(requestUrl, { signal: abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
          const containerVariant = this.closest(".product-card")?.querySelector(".variant-picker--left select.variant-option__select");
          if (containerVariant) {
            const newPrice = html.querySelector('product-price [ref="priceContainer"]');
            const currentPrice = this.closest(".product-card")?.querySelector('[ref="priceContainer"]');
            if (!newPrice || !currentPrice) return;
            if (currentPrice.innerHTML !== newPrice.innerHTML) {
              currentPrice.replaceWith(newPrice);
            }
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          // Superseded by a newer request on this variant picker (see the abort() above) —
          // that newer request owns recovering the UI, so don't fight it here.
          return;
        }
        console.error(error);
      })
      .finally(() => {
        // Only the most recent fetch for this variant picker gets to touch shared UI state;
        // an aborted, superseded fetch's finally should not undo what the newer one is doing.
        if (this.#abortController !== abortController) return;
        this.#abortController = undefined;

        // Guarantee the add-to-cart button can never get stuck disabled: it's disabled as
        // soon as a variant is selected (see #onVariantSelected in product-form.js) and
        // normally re-enabled once the fetched variant data arrives. If this fetch failed
        // or the response didn't contain what was needed to reach that path, force it back.
        const addToCartButtonContainer = /** @type {{ enable?: () => void } | null} */ (
          this.closest('.shopify-section, dialog, product-card')?.querySelector('add-to-cart-component')
        );
        addToCartButtonContainer?.enable?.();
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
